/**
 * Motor de comparacion: compara una ficha de alumno contra una ficha de referencia
 * (docente), campo por campo, segun el `compare` del field registry.
 * Consumido client-side por la app (detalle de la ficha del alumno).
 */

import {
  FICHA_FIELDS,
  getByPath,
  isFieldVisible,
  optionLabel,
  resolveOptions,
  type FieldSpec,
} from './fields'
import type { FichaCata, SeleccionDescriptores } from './types'

export type FieldComparisonStatus = 'match' | 'diff' | 'unanswered'

export interface FieldComparison {
  path: string
  /** Label del campo (español). */
  label: string
  section: FieldSpec['section']
  compare: FieldSpec['compare']
  status: FieldComparisonStatus
  /** Valor del alumno, ya resuelto a labels legibles. */
  own: string[]
  /** Valor del docente, ya resuelto a labels legibles. */
  reference: string[]
  /** Solo para `scale`: posiciones numericas 0-100. */
  ownScore?: number
  referenceScore?: number
}

export interface FichaComparison {
  matches: number
  comparable: number
  fields: FieldComparison[]
}

/** Margen de tolerancia de los sliders 0-100 (±5 = 5% del rango). */
export const SCALE_TOLERANCE = 5

/** -1 = "sin definir" en los sliders (ver createEmptyFicha). */
function isEmptyScore(v: unknown): boolean {
  return typeof v !== 'number' || Number.isNaN(v) || v < 0
}

/** Aplana un arbol de descriptores a un conjunto de ids canonicos seleccionados. */
export function flattenDescriptores(sel: SeleccionDescriptores | undefined): string[] {
  if (!sel) return []
  const out: string[] = []
  for (const grupo of Object.values(sel)) {
    for (const familia of Object.values(grupo)) {
      for (const id of familia.seleccionados ?? []) out.push(id)
    }
  }
  return out
}

function asStringArray(v: unknown): string[] {
  if (Array.isArray(v)) return v.filter((x): x is string => typeof x === 'string')
  return []
}

function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const setB = new Set(b)
  return a.every((x) => setB.has(x))
}

function multichoiceValues(field: FieldSpec, ficha: FichaCata): string[] {
  if (field.control === 'descriptor') {
    return flattenDescriptores(getByPath(ficha, field.path) as SeleccionDescriptores | undefined)
  }
  return asStringArray(getByPath(ficha, field.path))
}

/** Labels legibles de una multiseleccion (usa el catalogo del campo si aplica). */
function multichoiceLabels(field: FieldSpec, values: string[], ficha: FichaCata): string[] {
  const opts = resolveOptions(field, ficha)
  if (opts.length === 0) return values // descriptores: los ids ya son labels
  return values.map((v) => opts.find((o) => o.value === v)?.label ?? v)
}

function compareField(field: FieldSpec, own: FichaCata, reference: FichaCata): FieldComparison | null {
  const base = { path: field.path, label: field.labelKey, section: field.section, compare: field.compare }

  const visibleRef = isFieldVisible(field, reference)
  const visibleOwn = isFieldVisible(field, own)
  // Si el campo no aplica en ninguna de las dos fichas, se omite.
  if (!visibleRef && !visibleOwn) return null

  if (field.compare === 'scale') {
    const refVal = getByPath(reference, field.path)
    const ownVal = getByPath(own, field.path)
    if (!visibleRef || isEmptyScore(refVal)) return null // sin referencia contra que medir
    const refScore = refVal as number
    if (isEmptyScore(ownVal)) {
      return { ...base, status: 'unanswered', own: [], reference: [], referenceScore: refScore }
    }
    const ownScore = ownVal as number
    const status: FieldComparisonStatus =
      Math.abs(ownScore - refScore) <= SCALE_TOLERANCE ? 'match' : 'diff'
    return { ...base, status, own: [String(ownScore)], reference: [String(refScore)], ownScore, referenceScore: refScore }
  }

  if (field.compare === 'choice') {
    const refVal = (getByPath(reference, field.path) as string) ?? ''
    const ownVal = (getByPath(own, field.path) as string) ?? ''
    if (!visibleRef || refVal === '') return null
    const refLabels = [optionLabel(field, refVal, reference)]
    if (ownVal === '') return { ...base, status: 'unanswered', own: [], reference: refLabels }
    const status: FieldComparisonStatus = ownVal === refVal ? 'match' : 'diff'
    return { ...base, status, own: [optionLabel(field, ownVal, own)], reference: refLabels }
  }

  if (field.compare === 'multichoice') {
    const refVals = visibleRef ? multichoiceValues(field, reference) : []
    const ownVals = visibleOwn ? multichoiceValues(field, own) : []
    if (refVals.length === 0) return null // docente no seleccionó nada: nada que comparar
    const refLabels = multichoiceLabels(field, refVals, reference)
    if (ownVals.length === 0) return { ...base, status: 'unanswered', own: [], reference: refLabels }
    const status: FieldComparisonStatus = sameSet(ownVals, refVals) ? 'match' : 'diff'
    return { ...base, status, own: multichoiceLabels(field, ownVals, own), reference: refLabels }
  }

  if (field.compare === 'numeric') {
    const refRaw = String(getByPath(reference, field.path) ?? '').trim()
    const ownRaw = String(getByPath(own, field.path) ?? '').trim()
    const refNum = Number(refRaw)
    if (!visibleRef || refRaw === '' || Number.isNaN(refNum)) return null
    if (ownRaw === '') return { ...base, status: 'unanswered', own: [], reference: [refRaw] }
    const ownNum = Number(ownRaw)
    const tolerance = Math.abs(refNum) * 0.05
    const status: FieldComparisonStatus =
      !Number.isNaN(ownNum) && Math.abs(ownNum - refNum) <= tolerance ? 'match' : 'diff'
    return { ...base, status, own: [ownRaw], reference: [refRaw] }
  }

  return null
}

/**
 * Compara la ficha del alumno contra la de referencia del docente.
 * Solo cuentan los campos comparables donde el docente tiene una respuesta.
 */
export function compareFichas(own: FichaCata, reference: FichaCata): FichaComparison {
  const fields: FieldComparison[] = []
  let matches = 0
  let comparable = 0

  for (const field of FICHA_FIELDS) {
    if (field.compare === 'none') continue
    const result = compareField(field, own, reference)
    if (!result) continue
    fields.push(result)
    comparable += 1
    if (result.status === 'match') matches += 1
  }

  return { matches, comparable, fields }
}
